import React, { useEffect, useMemo } from "react";
import { useStyles } from "./styles";

import List from "@mui/material/List";
import { Box } from "@mui/material";

import CountryMenuItem from "./countryMenuItem";
import { useCountries } from "./../../../../hooks/useCountries";
import { Country } from "../../../../hooks/useGetModelsService/types";
import { SelectedCountry } from "../../CityBar";

type CountriesMenuProps = {
  onSelect?: () => void;
  onCountrySelect?: (c: SelectedCountry) => void;
  filter?: string;
};

type MenuCountry = Country & { soon?: boolean };

const EUROPE_FIXED: [string, string][] = [
  ["Estonia", "ee"],
  ["Latvia", "lv"],
  ["Lithuania", "lt"],
  ["Germany", "de"],
  ["Switzerland", "ch"],
  ["Croatia", "hr"],
  ["Poland", "pl"],
  ["Sweden", "se"],
  ["Finland", "fi"],
];
const LATAM_FIXED: [string, string][] = [["Argentina", "ar"], ["Ecuador", "ec"]];

const CountriesMenu: React.FC<CountriesMenuProps> = ({
  onSelect,
  onCountrySelect,
  filter = "",
}) => {
  const classes = useStyles();

  const { data: countries, refetch: fetchCountries } = useCountries();

  useEffect(() => {
    if (countries && countries.length === 0) {
      fetchCountries();
    }
  }, [countries, fetchCountries]);

  const { europe, latam } = useMemo(() => {
    const all: Country[] = countries ?? [];
    const latamNames = LATAM_FIXED.map(([n]) => n);
    const build = (base: MenuCountry[], fixed: [string, string][]) => {
      const out = [...base];
      fixed.forEach(([n, a]) => {
        if (!out.some((c) => c.name === n)) {
          const api = all.find((c) => c.name === n);
          out.push({
            id: api?.id ?? -1,
            imageUrl: api?.imageUrl ?? "",
            cities: [],
            ...api,
            name: n,
            acronym: api?.acronym || a,
            totalModels: 0,
            soon: true,
          } as MenuCountry);
        }
      });
      return out;
    };
    const withModels = all.filter((c) => (c.totalModels ?? 0) > 0);
    return {
      europe: build(
        withModels.filter((c) => !latamNames.includes(c.name)),
        EUROPE_FIXED
      ),
      latam: build(
        withModels.filter((c) => latamNames.includes(c.name)),
        LATAM_FIXED
      ),
    };
  }, [countries]);

  const normTxt = (s: string) =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  const matches = (n: string) => !filter.trim() || normTxt(n).includes(normTxt(filter));

  const renderList = (list: MenuCountry[]) => (
    <List component="nav" className={classes.appMenu} disablePadding>
      {[...list]
        .filter((c) => matches(c.name))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item, index) => (
        <CountryMenuItem
          {...item}
          key={item.name + index}
          onSelect={() => {
            onSelect?.();
          }}
          onCountryClick={onCountrySelect}
        />
      ))}
    </List>
  );

  return (
    <Box className={classes.countryList}>
      {europe.some((c) => matches(c.name)) && <h2 className={classes.continent}>Europe</h2>}
      {renderList(europe)}
      {latam.some((c) => matches(c.name)) && <h2 className={classes.continent}>Latin America</h2>}
      {renderList(latam)}
    </Box>
  );
};

export default CountriesMenu;
